import type { AtlBrowserStorage } from '@atlassian/browser-storage-controls/web-storage-service';

import { isRecord } from './is-record';

type BrowserStorage = Pick<AtlBrowserStorage, 'getItem' | 'setItem'>;

export interface CachedEntry<T> {
	expiresAt: number;
	// When the answer arrived; absent from entries written before it was recorded.
	generatedAt?: number;
	value: T;
}

/**
 * Returns the stored expiry alongside the value so an in-memory copy can be hydrated with the
 * entry's own deadline. Recomputing one here would quietly extend a nearly-expired entry every time
 * a new page load read it.
 */
export const readBrowserStorageCache = <T>(
	storage: BrowserStorage,
	key: string,
	parseValue: (value: unknown) => T | null,
	now: number,
): CachedEntry<T> | null => {
	try {
		const cached: unknown = JSON.parse(storage.getItem(key) ?? 'null');
		if (!isRecord(cached) || typeof cached.expiresAt !== 'number' || cached.expiresAt <= now) {
			return null;
		}

		const value = parseValue(cached.value);
		return value === null ? null : { expiresAt: cached.expiresAt, value };
	} catch {
		return null;
	}
};

export const writeBrowserStorageCache = <T>(
	storage: BrowserStorage,
	key: string,
	entry: CachedEntry<T>,
): void => {
	try {
		void storage.setItem(key, JSON.stringify(entry)).catch(() => undefined);
	} catch {
		// Caching is optional and must not affect the caller's behavior. A write blocked by consent
		// needs no handling here: the caller has already kept the value in memory for this page load.
	}
};
